import React from "react";

import { css } from "@emotion/core";

import { Colors } from "@zapier/design-system";

import { useApolloInjection } from "@zapier/checkout/esm/contexts/ApolloInjection";
import { Interval } from "@zapier/checkout/esm/types";
import { Currency, Price, useCurrencies } from "@zapier/currency";

import { tableItem } from "../CheckoutSummary";

export type SummaryLineItem = {
  amount: number;
  currency?: string;
  description?: string;
  id: string;
};

export type SummaryLineSubscription = {
  amount: number;
  currency?: string;
  id: string;
  plan?: {
    interval: Interval;
    name: string;
  };
};

type Props = {
  line: SummaryLineItem | SummaryLineSubscription;
};

const itemCss = css`
  display: flex;

  & + & {
    margin-top: 0;

    @media (min-width: 750px) {
      margin-top: 10px;
    }
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${Colors.neutral400};
  }
`;

const descriptionCss = css`
  flex: 1 0 0;
  padding-right: 20px;
`;

const priceCss = css`
  flex: 0 0 90px;
  text-align: right;
  white-space: nowrap;
`;

export const SummaryLine = ({ line }: Props) => {
  const { useQuery } = useApolloInjection();
  const { currencies } = useCurrencies({ useQuery });
  // Use the line item ISO code to get symbol etc from the currenciesMap
  const currency = currencies.find((c: Currency) => c.name === line.currency);
  // some invoice items dont come with a description (e.g. subscription items)
  let description;
  if ("description" in line) {
    description = line.description;
  } else if ("plan" in line) {
    description =
      line.plan && `${line.plan.name} (billed ${line.plan.interval}ly)`;
  }

  return (
    <div css={[itemCss && tableItem]}>
      <span css={descriptionCss}>{description}</span>
      <span css={priceCss}>
        <Price currency={currency} isLocalized={true}>
          {line.amount}
        </Price>
      </span>
    </div>
  );
};
